package ai.jessie.web.theme

import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.silk.components.forms.CheckboxVars
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.extendedByBase
import com.varabyte.kobweb.silk.style.until
import org.jetbrains.compose.web.css.px

private const val CHECKBOX_RADIUS_PX = 5
private const val CHECKBOX_SIZE_PX = 18
private const val CHECKBOX_GAP_PX = 10
private const val CHECKBOX_FONT_PX = 14

/** `<fieldset>` without the browser's box; disabling it still disables every control. */
val FieldsetStyle = CssStyle.base {
    css(
        "min-width" to "0",
        "border" to "0",
        "padding" to "0",
        "margin" to "0",
    )
}

/** Caption of a fieldset shown as a card: sits above the card body like a card head. */
val LegendStyle = CssStyle.base {
    css("padding" to "20px 20px 0", "font-size" to "15px", "font-weight" to "600")
}

/** Vertical run of form fields with the slide-over's field gap. */
val FormStackStyle = CssStyle.base {
    css("display" to "flex", "flex-direction" to "column", "gap" to "18px")
}

/** `<label>` wrapping a caption and its control. */
val FieldStyle = CssStyle.base {
    css(
        "display" to "flex",
        "flex-direction" to "column",
        "gap" to "6px",
        "font-size" to "13px",
        "font-weight" to "600",
    )
}

val FieldOptionalStyle = CssStyle.base {
    css("color" to InkPlaceholderVar.value(), "font-weight" to "400")
}

/** Correction shown under an invalid field. */
val FieldErrorStyle = CssStyle.base {
    css(
        "margin" to "0",
        "font-size" to "13px",
        "font-weight" to "400",
        "color" to DangerVar.value(),
    )
}

/** Text input; apply to Silk's `TextInput` with `UnstyledInputVariant`, or to a plain input. */
val FieldInputStyle = CssStyle {
    base {
        css(
            "display" to "block",
            "width" to "100%",
            "box-sizing" to "border-box",
            "height" to JessieTokens.INPUT_HEIGHT,
            "padding" to "0 12px",
            "border" to "1px solid ${LineInputVar.value()}",
            "border-radius" to JessieTokens.RADIUS_CONTROL,
            "background" to SurfaceVar.value(),
            "font-size" to "14px",
            "font-weight" to "400",
            "color" to InkVar.value(),
        )
    }
    cssRule("::placeholder") { css("color" to InkPlaceholderVar.value(), "opacity" to "1") }
    cssRule(":focus") {
        css(
            "outline" to "none",
            "border-color" to PrimaryVar.value(),
            "box-shadow" to "0 0 0 3px ${FocusRingVar.value()}",
        )
    }
    cssRule("[readonly]") { css("background" to SurfaceMutedVar.value()) }
    // After `:focus` so an invalid field keeps the danger line while focused.
    cssRule("[aria-invalid=\"true\"]") {
        css(
            "border-color" to DangerVar.value(),
            "box-shadow" to "0 0 0 3px ${DangerTintVar.value()}",
        )
    }
}

/** Numeric input: Dank Mono with tabular figures. */
val NumInputStyle = FieldInputStyle.extendedByBase {
    css("font-family" to JessieTokens.FONT_NUM, "font-variant-numeric" to "tabular-nums")
}

val TextAreaStyle = FieldInputStyle.extendedByBase {
    css(
        "height" to "auto",
        "min-height" to "88px",
        "padding" to "10px 12px",
        "resize" to "vertical",
    )
}

/** Re-skins Silk's `Checkbox`: 18 px box, 5 px radius; colours come from the Silk palette. */
val CheckStyle = CssStyle.base {
    Modifier.setVariable(CheckboxVars.BorderColor, LineInputVar.value())
        .setVariable(CheckboxVars.BorderRadius, CHECKBOX_RADIUS_PX.px)
        .setVariable(CheckboxVars.BorderWidth, 1.px)
        .setVariable(CheckboxVars.Size, CHECKBOX_SIZE_PX.px)
        .setVariable(CheckboxVars.Spacing, CHECKBOX_GAP_PX.px)
        .setVariable(CheckboxVars.FontSize, CHECKBOX_FONT_PX.px)
        .setVariable(CheckboxVars.UncheckedBackgroundColor, SurfaceVar.value())
        .setVariable(CheckboxVars.FocusOutlineColor, FocusRingVar.value())
}

val CheckHintStyle = CssStyle.base {
    css("display" to "block", "font-size" to "13px", "color" to InkMutedVar.value())
}

/**
 * Radio choices with descriptions drawn as selectable cards (Aufwand / Festpreis), side by side.
 */
val ChoiceCardsStyle = CssStyle {
    base {
        css(
            "display" to "grid",
            "grid-template-columns" to "repeat(2, minmax(0, 1fr))",
            "gap" to "12px",
        )
    }
    until(Breakpoint.LG) { css("grid-template-columns" to "minmax(0, 1fr)") }
}

/** One choice: the radio beside its title and description; the checked card is highlighted. */
val ChoiceCardStyle = CssStyle {
    base {
        css(
            "display" to "flex",
            "align-items" to "flex-start",
            "gap" to "10px",
            "padding" to "14px 16px",
            "border" to "1px solid ${LineVar.value()}",
            "border-radius" to JessieTokens.RADIUS_CARD,
            "background" to SurfaceVar.value(),
            "font-size" to "14px",
            "font-weight" to "600",
            "cursor" to "pointer",
        )
    }
    cssRule(":has(input:checked)") {
        css("border-color" to PrimaryVar.value(), "background" to PrimaryTintSoftVar.value())
    }
    cssRule(":has(input:focus-visible)") {
        css("box-shadow" to "0 0 0 3px ${FocusRingVar.value()}")
    }
    cssRule(" input") {
        css("margin" to "2px 0 0", "accent-color" to PrimaryVar.value(), "flex-shrink" to "0")
    }
}

/** The muted description under a choice title. */
val ChoiceHintStyle = CssStyle.base {
    css(
        "display" to "block",
        "margin-top" to "4px",
        "font-size" to "13px",
        "font-weight" to "400",
        "color" to InkMutedVar.value(),
    )
}
